import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import AdminLayout from './components/admin/AdminLayout'
import FormList from './components/admin/FormList'
import FormBuilderPage from './pages/FormBuilderPage'
import LiveQuizBuilderPage from './pages/LiveQuizBuilderPage'
import SubmissionsPage from './pages/SubmissionsPage'
import PublicFormPage from './pages/PublicFormPage'
import PublicResultsPage from './pages/PublicResultsPage'
import { ProtectedRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute'
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage, AcceptInvitationPage, VerifyEmailPage, CompleteProfilePage } from './pages/auth'
import CookieConsent from './components/ui/CookieConsent'
import MobileRedirect from './components/MobileRedirect'
import { initGA, usePageTracking, reportWebVitals } from './lib/analytics'
import { ensureCSRFToken } from './lib/api'

// Superadmin pages
import SuperadminDashboardPage from './pages/superadmin/DashboardPage'
import SuperadminUsersPage from './pages/superadmin/UsersPage'
import SuperadminInvitationsPage from './pages/superadmin/InvitationsPage'
import SuperadminAuditLogsPage from './pages/superadmin/AuditLogsPage'
import SuperadminSettingsPage from './pages/superadmin/SettingsPage'
import SuperadminUserDetailPage from './pages/superadmin/UserDetailPage'

// Settings pages
import ProfilePage from './pages/settings/ProfilePage'
import SecurityPage from './pages/settings/SecurityPage'
import ApiKeysPage from './pages/settings/ApiKeysPage'
import PrivacySettingsPage from './pages/settings/PrivacySettingsPage'
import InvitationsPage from './pages/settings/InvitationsPage'
import GroupsPage from './pages/settings/GroupsPage'

// Legal pages
import TermsOfServicePage from './pages/legal/TermsOfServicePage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import AboutPage from './pages/legal/AboutPage'
import CookiePolicyPage from './pages/legal/CookiePolicyPage'

// Public pages
import HomePage from './pages/HomePage'
import ApiDocsPage from './pages/docs/ApiDocsPage'

// Blog pages
import { BlogListPage, BlogPostPage, BlogAdminPage } from './pages/blog'

// Landing pages
import { EducationPage, EventsPage, BusinessPage } from './pages/landing'

// Demo pages
import DemoQuizPage from './pages/demo/DemoQuizPage'
import DemoPollPage from './pages/demo/DemoPollPage'

// E-commerce pages (legacy)
import ProductsPage from './pages/ProductsPage'
import OrdersPage from './pages/OrdersPage'
import PortalPage from './pages/PortalPage'


// Live Quiz pages
import LiveQuizHostPage from './pages/LiveQuizHostPage'
import LiveQuizPlayPage from './pages/LiveQuizPlayPage'

// Mobile pages
import { MobileHomePage, MobileFormsPage, MobileFormBuilderPage, MobileLiveQuizBuilderPage } from './pages/mobile'

export default function App() {
  // Initialize on mount
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    reportWebVitals();

    // Initialize CSRF protection
    ensureCSRFToken();
  }, []);

  // Track page views on route changes
  usePageTracking();

  return (
    <MobileRedirect>
    <Routes>
      {/* Auth routes (public only - redirect if already logged in) */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />

      {/* Formspace routes (protected) */}
      <Route
        path="/formspace"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<FormList />} />
        <Route path="builder/:id" element={<FormBuilderPage />} />
        <Route path="live-quiz/:id" element={<LiveQuizBuilderPage />} />
        <Route path="products/:id" element={<ProductsPage />} />
        <Route path="orders/:id" element={<OrdersPage />} />
        <Route path="portal" element={<PortalPage />} />
        <Route path="forms/:id/submissions" element={<SubmissionsPage />} />
      </Route>

      {/* Superadmin routes (protected - superadmin only) */}
      <Route
        path="/superadmin"
        element={
          <ProtectedRoute requiredRoles={['superadmin']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<SuperadminDashboardPage />} />
        <Route path="users" element={<SuperadminUsersPage />} />
        <Route path="users/:id" element={<SuperadminUserDetailPage />} />
        <Route path="invitations" element={<SuperadminInvitationsPage />} />
        <Route path="audit-logs" element={<SuperadminAuditLogsPage />} />
        <Route path="settings" element={<SuperadminSettingsPage />} />
        <Route path="blog" element={<BlogAdminPage />} />
      </Route>

      {/* Settings routes (protected) */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/settings/profile" replace />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="privacy" element={<PrivacySettingsPage />} />
        <Route path="invitations" element={<InvitationsPage />} />
        <Route path="teams" element={<GroupsPage />} />
      </Route>

      {/* Legal pages (public) */}
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />

      {/* Documentation (public) */}
      <Route path="/docs/api" element={<ApiDocsPage />} />

      {/* Blog (public) */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* Landing pages (public) */}
      <Route path="/education" element={<EducationPage />} />
      <Route path="/business" element={<BusinessPage />} />
      <Route path="/events" element={<EventsPage />} />

      {/* Demo pages (public) */}
      <Route path="/demo/quiz" element={<DemoQuizPage />} />
      <Route path="/demo/poll" element={<DemoPollPage />} />

      {/* Public form routes (no auth required) */}
      {/* /f/ - Regular forms */}
      <Route path="/f/:slug" element={<PublicFormPage />} />
      {/* /p/ - Polls */}
      <Route path="/p/:slug" element={<PublicFormPage />} />
      {/* /q/ - Quizzes (simple mode) */}
      <Route path="/q/:slug" element={<PublicFormPage />} />
      {/* /l/ - Live quizzes redirect to join page with PIN */}
      <Route path="/l/:slug" element={<PublicFormPage />} />
      {/* /product/ - Product pages */}
      <Route path="/product/:slug" element={<PublicFormPage />} />
      {/* /order/ - Order forms */}
      <Route path="/order/:slug" element={<PublicFormPage />} />

      {/* Public results route (no auth required - uses secure key) */}
      <Route path="/results/:key" element={<PublicResultsPage />} />

      {/* Invitation acceptance route */}
      <Route path="/invite/:token" element={<AcceptInvitationPage />} />

      {/* Email verification route */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* Complete profile route - for users who need to add display name */}
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <CompleteProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Live Quiz routes */}
      {/* Host routes (protected) */}
      <Route
        path="/live/host/:formId"
        element={
          <ProtectedRoute>
            <LiveQuizHostPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live/session/:sessionId/host"
        element={
          <ProtectedRoute>
            <LiveQuizHostPage />
          </ProtectedRoute>
        }
      />
      {/* Player routes (public - no auth required) */}
      <Route path="/play" element={<LiveQuizPlayPage />} />
      <Route path="/join" element={<LiveQuizPlayPage />} />

      {/* Home page (public landing page) */}
      <Route path="/" element={<HomePage />} />

      {/* Mobile App routes (protected) */}
      <Route
        path="/m/forms"
        element={
          <ProtectedRoute>
            <MobileFormsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/m/builder/:id"
        element={
          <ProtectedRoute>
            <MobileFormBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/m/live-quiz/:id"
        element={
          <ProtectedRoute>
            <MobileLiveQuizBuilderPage />
          </ProtectedRoute>
        }
      />
      {/* Mobile Home/Landing Page (public) */}
      <Route path="/m" element={<MobileHomePage />} />
    </Routes>

    {/* Cookie Consent Banner */}
    <CookieConsent />
    </MobileRedirect>
  )
}
